import { useState } from "react";
import { vocabularize } from "src/Common/vocab";
import Viewer from "react-viewer";
import { formatDate } from "src/Common/common";
import PDFPreviewModal from "src/Pages/WO/Components/pdfpreview";

const getStatusLabel = (ticket) => {
  if (typeof ticket?.status === "string" && ticket.status.trim()) {
    return ticket.status;
  }
  if (typeof ticket?.status === "number") {
    if (ticket.status === 100) return "Closed";
    if (ticket.status === 0) return "Open";
  }
  if (ticket?.isClosed) return "Closed";
  return "Open";
};

const InfoSection = ({ ticket }) => {
  const attachments = Array.isArray(ticket?.attachments) ? ticket.attachments : [];
  const [isImageViewerShown, setIsImageViewerShown] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewImageActiveIndex, setPreviewImageActiveIndex] = useState(0);
  const [isPDFViewerShown, setIsPDFViewerShown] = useState(false);
  const [previewPDFs, setPreviewPDFs] = useState([]);
  const [previewPDFActiveIndex, setPreviewPDFActiveIndex] = useState(0);

  const isPdfFile = (value) => {
    if (!value) return false;
    const normalized = String(value).split("?")[0].toLowerCase();
    return normalized.endsWith(".pdf");
  };

  const handleAttachmentClick = (attachment) => {
    if (!attachments.length) return;

    if (isPdfFile(attachment?.url)) {
      const pdfUrls = [];
      let activeIndex = 0;

      attachments.forEach((item) => {
        if (isPdfFile(item?.url)) {
          if (item?.url === attachment?.url) {
            activeIndex = pdfUrls.length;
          }
          pdfUrls.push(item.url);
        }
      });

      setPreviewPDFs(pdfUrls);
      setPreviewPDFActiveIndex(activeIndex);
      setIsPDFViewerShown(true);
    } else {
      const imageFiles = [];
      let activeIndex = 0;

      attachments.forEach((item) => {
        if (!isPdfFile(item?.url) && item?.url) {
          if (item.url === attachment?.url) {
            activeIndex = imageFiles.length;
          }
          imageFiles.push({ src: item.url, alt: "" });
        }
      });

      setPreviewImages(imageFiles);
      setPreviewImageActiveIndex(activeIndex);
      setIsImageViewerShown(true);
    }
  };

  return (
    <div className="border border-gray-400">
      <Viewer
        visible={isImageViewerShown}
        onClose={() => setIsImageViewerShown(false)}
        images={previewImages}
        activeIndex={previewImageActiveIndex}
      />
      <PDFPreviewModal
        PDFs={previewPDFs}
        activeIndex={previewPDFActiveIndex}
        shown={isPDFViewerShown}
        onCancel={() => setIsPDFViewerShown(false)}
        useDirectUrl
      />
      <div className="flex items-center justify-between border-b border-gray-400 px-4 py-3 ">
        <span className="font-OpenSansRegular font-[700] text-[15px] text-titleColor">
          Ticket #{ticket?.number || ""}
        </span>
        <div className="flex items-center gap-1">
          <span className="font-OpenSansRegular font-[700] text-[15px] text-titleColor">Status:</span>
          <div
            className={`px-4 h-[22px] flex items-center justify-center rounded-[11px] font-OpenSansRegular font-[400] text-titleColor text-[12px] ${getStatusLabel(ticket) === "Open" ? "bg-wo_color2" : "bg-wo_color6"
              }`}
          >
            {getStatusLabel(ticket)}
          </div>
        </div>
      </div>

      <div className="px-4 pb-4">
        <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">Subject</span>
        <p className="mt-1 font-OpenSansRegular text-[12px] text-titleColor">{ticket?.subject || "-"}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">{vocabularize("WO #")}</span>
          <span className="font-OpenSansRegular text-[12px] text-titleColor">{ticket?.woNumber || "-"}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">PO #</span>
          <span className="font-OpenSansRegular text-[12px] text-titleColor">{ticket?.poNumber || "-"}</span>
        </div>
      </div>

      <div className="px-4 pb-4">
        <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">Description</span>
        <p className="mt-1 font-OpenSansRegular text-[12px] text-titleColor">{ticket?.description || "-"}</p>
      </div>

      <div className="px-4 pb-4">
        <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">Screenshots</span>
        <div className="mt-2 flex flex-wrap gap-3">
          {attachments.map((attachment, index) => {
            const src = attachment?.url || null;
            return (
              <div
                key={attachment?.id || attachment?.url || index}
                onClick={() => src && handleAttachmentClick(attachment)}
                className="h-[74px] w-[74px] rounded-[6px] border border-gray-400 bg-[#f7f7fb] flex items-center justify-center overflow-hidden cursor-pointer"
              >
                {src ? <img src={src} alt="Ticket attachment" className="h-full w-full object-cover" /> : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 pb-4">
        <div className="flex flex-col gap-1 text-[12px] text-titleColor">
          <div className="flex items-center gap-1">
            <span className="font-OpenSansRegular font-[700]">Name:</span>
            <span className="font-OpenSansRegular">{ticket?.createdByUser?.name || (ticket?.createdByCustomerUser ? `${ticket.createdByCustomerUser.name} (customer)` : "N/A")}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-OpenSansRegular font-[700]">Email:</span>
            <span className="font-OpenSansRegular">{ticket?.createdByUser?.email || ticket?.createdByCustomerUser?.email || "N/A"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-OpenSansRegular font-[700]">Phone:</span>
            <span className="font-OpenSansRegular">{ticket?.createdByUser?.phone || "N/A"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-OpenSansRegular font-[700]">Platform:</span>
            <span className="font-OpenSansRegular">{ticket?.platform || "N/A"}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-OpenSansRegular font-[700]">App Version:</span>
            <span className="font-OpenSansRegular">{ticket?.appVersion || "N/A"}</span>
          </div>
          {ticket?.createdAt ? (
            <div className="flex items-center gap-1">
              <span className="font-OpenSansRegular font-[700]">Opened:</span>
              <span className="font-OpenSansRegular">
                {formatDate(ticket.createdAt, "MM/DD/YYYY - h:mm A")}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default InfoSection;
